import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRegisterSW } from "virtual:pwa-register/react";

/** Cada cuánto forzamos la búsqueda de una versión nueva del SW. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const UPDATE_RELOAD_FALLBACK_MS = 1500;

export default function UpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const reloadStartedRef = useRef(false);
  const [updating, setUpdating] = useState(false);

  const reloadForUpdatedApp = () => {
    if (reloadStartedRef.current) return;
    reloadStartedRef.current = true;
    window.location.reload();
  };

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
    },
    onRegisterError() {
      // Sin SW (navegador viejo, header faltante) la app sigue funcionando
      // como web normal; no hay nada que mostrarle al usuario.
    },
  });

  const applyUpdate = () => {
    setUpdating(true);
    const registration = registrationRef.current;
    void registration?.update().catch(() => {});
    registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
    void updateServiceWorker(false);
    window.setTimeout(reloadForUpdatedApp, UPDATE_RELOAD_FALLBACK_MS);
  };

  useEffect(() => {
    const check = () => {
      registrationRef.current?.update().catch(() => {});
    };
    const intervalId = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onControllerChange = () => reloadForUpdatedApp();
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  // Eduardo 2026-09-20: con varios releases al día, admin y superadmin seguían
  // operando con el bundle anterior hasta pulsar "Actualizar" (de ahí varios
  // "bugs" fantasma ya corregidos en producción). En pantallas de staff la
  // versión nueva se aplica sola AL NAVEGAR a otra pantalla: en ese momento el
  // formulario anterior ya se descartó, así que nunca se pierde nada a medias.
  // El jugador conserva el banner manual.
  const location = useLocation();
  const isStaffRoute = /^\/(admin|superadmin)(\/|$)/.test(location.pathname);
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    const previous = lastPathRef.current;
    lastPathRef.current = location.pathname;
    if (!isStaffRoute || previous === location.pathname) return;
    if (!needRefresh) {
      // Sin versión pendiente: aprovecha la navegación para preguntar si ya hay
      // una (la revisión periódica es cada hora); se aplicará en la siguiente.
      registrationRef.current?.update().catch(() => {});
      return;
    }
    applyUpdate();
    // applyUpdate es estable en la práctica (solo usa refs y setState).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefresh, isStaffRoute, location.pathname]);

  if (!needRefresh) return null;

  return (
    <div className="pwa-banner pwa-banner--update" role="status">
      <span>Hay una nueva versión de Torneos123.</span>
      <button
        type="button"
        className="btn btn--primary pwa-banner__action"
        disabled={updating}
        onClick={applyUpdate}
      >
        {updating ? "Actualizando…" : "Actualizar"}
      </button>
    </div>
  );
}
